/**
 * THE PASS TARGET LIST'S ONE COMPILE FUNCTION — plan-021 DOM (plan-021 ED-3, ED-4, §2.10; design §33.4.1, §33.4.3;
 * ADR 0074, proposed; glossary §6.37 *pass target*).
 *
 * The customer authors the list in the portal; the api stores it as a draft answer under
 * `config_document.draft.voice_agent_pass_target[]` (snake_case like every draft answer, ADR 0044's pattern), and the
 * document key `AgentRuntimeConfig.voiceAgentPass` is compiled from it by {@link voiceAgentPassOf} — the one function the
 * factory's build (FAC) and the api's derived-draft route (API-CFG) both call, so "the factory compiles" (ADR 0002) holds
 * without a factory round trip for a list edit.
 *
 * ```
 * draft answer (snake_case)                      document key (camelCase)
 *   target_key ─────────────────────────────▶    targetKey                    kept: assigned once (targetKeyAssigned)
 *   target_voice_agent_id ──────────────────▶    targetVoiceAgentId
 *   display_name ──── voiceAgentPassDisplayNameOf ▶ displayName              control strings removed, ≤ 80 (D-19)
 *   when ───────────────────────────────────▶    when
 *   mode ───────────────────────────────────▶    mode
 *   may_pass_back ──────────────────────────▶    mayPassBack
 *   note ──── cold, or empty ⇒ null ─────────▶    note
 * ```
 *
 * Mechanical and pure. The database checks (the same workspace, the target's state, its template, the audience) are not
 * here: the api runs them at save, the factory at build, and the runtime re-checks at the pass (design §33.7).
 */
import { z } from "zod";
import { voiceAgentId } from "../id/codec.js";
import { idSchema } from "../id/schema.js";
import { SINGLE_LINE_WITHOUT_CONTROL_STRING, VOICE_AGENT_PASS_MODE, VOICE_AGENT_PASS_NOTE_CHARACTER_LIMIT, VOICE_AGENT_PASS_TARGET_KEY, VOICE_AGENT_PASS_TARGET_LIMIT, VOICE_AGENT_PASS_WHEN_CHARACTER_LIMIT, voiceAgentPassDisplayNameOf, } from "./voice-agent-pass.js";
/** The draft answer's key under `config_document.draft` (ED-4). */
export const VOICE_AGENT_PASS_TARGET_ANSWER_KEY = "voice_agent_pass_target";
/** The longest raw display name an answer may hold — the compile cuts the document's to 80 (design §33.4.2). */
const DISPLAY_NAME_ANSWER_CHARACTER_LIMIT = 200;
const SINGLE_LINE = "must be one line without a control character, <| or |>";
const VoiceAgentPassTargetAnswerEntrySchema = z
    .object({
    target_key: z.enum(VOICE_AGENT_PASS_TARGET_KEY),
    target_voice_agent_id: idSchema(voiceAgentId),
    display_name: z
        .string()
        .min(1)
        .max(DISPLAY_NAME_ANSWER_CHARACTER_LIMIT)
        .refine((name) => voiceAgentPassDisplayNameOf(name).length > 0, "names nothing once control text is removed"),
    when: z
        .string()
        .min(1)
        .max(VOICE_AGENT_PASS_WHEN_CHARACTER_LIMIT)
        .regex(SINGLE_LINE_WITHOUT_CONTROL_STRING, SINGLE_LINE)
        .regex(/\S/u, "must not be blank"),
    mode: z.enum(VOICE_AGENT_PASS_MODE),
    may_pass_back: z.boolean(),
    note: z
        .string()
        .max(VOICE_AGENT_PASS_NOTE_CHARACTER_LIMIT)
        .regex(SINGLE_LINE_WITHOUT_CONTROL_STRING, SINGLE_LINE)
        .nullable(),
})
    .strict();
/**
 * The draft answer's reader (zod, snake_case): refuses what the save would refuse — an unknown field, a text out of
 * bounds or holding a control string (D-19), more than {@link VOICE_AGENT_PASS_TARGET_LIMIT} entries, a key or a target
 * named twice, a note on a cold entry. The factory reads the draft with it (a malformed answer fails the build as an
 * input fault) and the api checks the answers it writes with it.
 */
export const VoiceAgentPassTargetAnswerSchema = z
    .array(VoiceAgentPassTargetAnswerEntrySchema)
    .max(VOICE_AGENT_PASS_TARGET_LIMIT)
    .superRefine((answer, ctx) => {
    const seenKey = new Set();
    const seenTarget = new Set();
    answer.forEach((entry, index) => {
        if (seenKey.has(entry.target_key)) {
            ctx.addIssue({ code: "custom", path: [index, "target_key"], message: `duplicate pass target key ${entry.target_key}` });
        }
        seenKey.add(entry.target_key);
        if (seenTarget.has(entry.target_voice_agent_id)) {
            ctx.addIssue({
                code: "custom",
                path: [index, "target_voice_agent_id"],
                message: "a VoiceAgent is listed as a pass target more than once",
            });
        }
        seenTarget.add(entry.target_voice_agent_id);
        if (entry.mode === "cold" && entry.note !== null) {
            ctx.addIssue({ code: "custom", path: [index, "note"], message: "a cold pass tells the target nothing: note must be null" });
        }
    });
});
/**
 * Answers → the document key. Pure. `[]` is no targets. The order is the portal's; keys are never renumbered.
 *
 * plan-021 CONTRACT (Release 2): the answer is required. Release 1 also took `undefined` (never answered) as no targets;
 * every caller already decides that itself — the factory's build reads its source and passes `[]` for none
 * (`voiceAgentPassTargetAnswerOfBuild`), the api's derived draft reads the live key — so an absent list is never read here.
 */
export function voiceAgentPassOf(answer) {
    const target = answer.map((entry) => Object.freeze({
        targetKey: entry.target_key,
        targetVoiceAgentId: entry.target_voice_agent_id,
        displayName: voiceAgentPassDisplayNameOf(entry.display_name),
        when: entry.when,
        mode: entry.mode,
        mayPassBack: entry.may_pass_back,
        note: entry.mode === "cold" || entry.note === null || entry.note.trim().length === 0 ? null : entry.note,
    }));
    return Object.freeze({ target: Object.freeze(target) });
}
/** Raised by {@link targetKeyAssigned} when every key is taken — the save refuses a ninth entry before it is asked. */
export class VoiceAgentPassTargetKeyExhaustedError extends Error {
    constructor() {
        super(`every pass target key is assigned (at most ${VOICE_AGENT_PASS_TARGET_LIMIT} targets)`);
        this.name = "VoiceAgentPassTargetKeyExhaustedError";
    }
}
/**
 * The lowest free `target_N` for a new entry, given the keys already assigned (ED-3). A key once assigned stays with its
 * entry, so removing `target_2` leaves the gap for the next new entry and renames nobody. Throws past the limit.
 */
export function targetKeyAssigned(existing) {
    const taken = new Set(existing);
    const free = VOICE_AGENT_PASS_TARGET_KEY.find((key) => !taken.has(key));
    if (free === undefined)
        throw new VoiceAgentPassTargetKeyExhaustedError();
    return free;
}
//# sourceMappingURL=voice-agent-pass-compile.js.map